/**
 * PokerTable — Renders the oval poker table with player positions
 *
 * Displays up to 8 player seats, community cards, pot, dealer/blind
 * markers, active player highlight with timer, and card animations.
 *
 * Requirements: 11.5, 17.1–17.9, 33.4, 33.5
 */

import React from 'react';
import {
  PokerTableProps,
  VisiblePlayerState,
  Card,
  ActionRecord,
} from '../types';
import { CardDisplay } from './CardDisplay';
import './PokerTable.css';

/** Get the last action for a player in the current hand */
function getLastAction(
  actions: ActionRecord[],
  playerId: string
): ActionRecord | undefined {
  for (let i = actions.length - 1; i >= 0; i--) {
    if (actions[i].playerId === playerId) return actions[i];
  }
  return undefined;
}

function formatActionLabel(action: ActionRecord): string {
  switch (action.action) {
    case 'fold':
      return 'Fold';
    case 'check':
      return 'Check';
    case 'call':
      return `Call ${action.amount}`;
    case 'raise':
      return `Raise ${action.amount}`;
    case 'all-in':
      return `All-In ${action.amount}`;
    default:
      return action.action;
  }
}

export const PokerTable: React.FC<PokerTableProps> = ({
  gameState,
  currentPlayerId,
  isMultiplayer,
  turnTimeRemaining,
}) => {
  const {
    players,
    communityCards,
    pots,
    currentPlayerIndex,
    handPhase,
    actionHistory,
  } = gameState;

  const currentTurnPlayerId =
    currentPlayerIndex >= 0 && currentPlayerIndex < players.length
      ? players[currentPlayerIndex].id
      : null;

  const totalPot = pots.reduce((sum, p) => sum + p.amount, 0);
  // Also add current round bets not yet collected
  const currentBets = players.reduce((sum, p) => sum + p.currentBet, 0);
  const displayPot = totalPot + currentBets;

  const isShowdown = handPhase === 'showdown' || handPhase === 'complete';

  return (
    <div className="poker-table-container" role="region" aria-label="Poker table">
      <div className="poker-table">
        <div className="poker-table__inner">
          {/* Community cards */}
          <div className="poker-table__community" aria-label="Community cards">
            {communityCards.map((card: Card, i: number) => (
              <div key={i} className="poker-card-deal">
                <CardDisplay
                  card={card}
                  size="medium"
                  animateReveal={true}
                />
              </div>
            ))}
            {/* Placeholder slots for unrevealed community cards */}
            {Array.from({ length: Math.max(0, 5 - communityCards.length) }).map(
              (_, i) => (
                <CardDisplay
                  key={`empty-${i}`}
                  card={null}
                  size="medium"
                  faceDown
                />
              )
            )}
          </div>

          {/* Pot display */}
          {displayPot > 0 && (
            <div className="poker-table__pot" aria-live="polite">
              <span className="poker-table__pot-label">Pot</span>
              {displayPot}
              {pots.length > 1 && (
                <span className="poker-table__pot-label">
                  ({pots.length} pots)
                </span>
              )}
            </div>
          )}
        </div>

        {/* Player seats */}
        {players.map((player: VisiblePlayerState) => {
          const isActive = currentTurnPlayerId === player.id;
          const isFolded = player.status === 'folded';
          const isEliminated = player.status === 'eliminated';
          const isCurrentPlayer = player.id === currentPlayerId;
          const lastAction = getLastAction(actionHistory, player.id);
          const showHoleCards =
            isCurrentPlayer ||
            player.showCards ||
            isShowdown;

          const seatClass = [
            'poker-seat',
            `poker-seat--${player.seatIndex}`,
            isActive ? 'poker-seat--active' : '',
            isFolded ? 'poker-seat--folded' : '',
            isEliminated ? 'poker-seat--eliminated' : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <div
              key={player.id}
              className={seatClass}
              aria-label={`${player.name}, ${player.chipStack} chips${isActive ? ', current turn' : ''}`}
            >
              {/* Hole cards */}
              <div className="poker-seat__cards">
                {player.holeCards.length > 0 && showHoleCards
                  ? player.holeCards.map((card, i) => (
                      <CardDisplay
                        key={i}
                        card={card}
                        size="small"
                        animateReveal={isShowdown && !isCurrentPlayer}
                      />
                    ))
                  : player.status !== 'eliminated' &&
                    player.status !== 'folded' &&
                    handPhase !== 'pre-deal' &&
                    handPhase !== 'complete'
                    ? [0, 1].map((i) => (
                        <CardDisplay
                          key={i}
                          card={null}
                          size="small"
                          faceDown
                        />
                      ))
                    : null}
              </div>

              {/* Player info */}
              <div className="poker-seat__info">
                <div className="poker-seat__name">{player.name}</div>
                {isEliminated ? (
                  <div className="poker-seat__busted">Busted</div>
                ) : (
                  <div className="poker-seat__chips">
                    💰 {player.chipStack}
                  </div>
                )}

                {/* Last action */}
                {lastAction && !isEliminated && (
                  <div
                    className={`poker-seat__action poker-seat__action--${lastAction.action}`}
                  >
                    {formatActionLabel(lastAction)}
                  </div>
                )}

                {/* Markers */}
                <div className="poker-seat__markers">
                  {player.isDealer && (
                    <span className="poker-seat__marker poker-seat__marker--dealer">
                      D
                    </span>
                  )}
                  {player.isSmallBlind && (
                    <span className="poker-seat__marker poker-seat__marker--sb">
                      SB
                    </span>
                  )}
                  {player.isBigBlind && (
                    <span className="poker-seat__marker poker-seat__marker--bb">
                      BB
                    </span>
                  )}
                </div>

                {/* Timer for active player in multiplayer */}
                {isActive && turnTimeRemaining !== undefined && (
                  <div
                    className={`poker-seat__timer ${
                      turnTimeRemaining <= 10
                        ? 'poker-seat__timer--warning'
                        : ''
                    }`}
                    aria-live="polite"
                  >
                    ⏱ {turnTimeRemaining}s
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PokerTable;
